import type { ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { clubAccent, clubInitials } from "@/lib/clubs/clubAccent";
import { useClubDetails } from "@/lib/clubs/gatherings";
import type { OrganizerClub } from "@/lib/clubs/queries";
import { UserMenu } from "@/components/UserMenu/UserMenu";
import { useAccessibleSurfaces } from "@/lib/auth/useAccessibleSurfaces";
import styles from "./AppSidebar.module.scss";

const COMING_SOON = [
  "Members",
  "Invites",
  "Join requests",
  "Settings",
  "Create club",
];

type AppSidebarProps =
  | {
      mode: "preview";
      clubs: OrganizerClub[];
      selectedId: string;
      onSelect: (id: string) => void;
    }
  | {
      mode: "navigate";
      clubs: OrganizerClub[];
    };

export function AppSidebar(props: AppSidebarProps) {
  const { surfaces } = useAccessibleSurfaces();

  return (
    <aside className={styles.sidebar} aria-label="Plotlounge navigation">
      <Link to="/" className={styles.brand}>
        <span className={styles.brandMark}>P</span>
        <span className={styles.brandName}>Plotlounge</span>
      </Link>

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <span>Your clubs</span>
          <span className={styles.sectionCount}>{props.clubs.length}</span>
        </div>
        <ul className={styles.list}>
          {props.clubs.map((club) =>
            props.mode === "preview" ? (
              <PreviewClubItem
                key={club.id}
                club={club}
                isActive={club.id === props.selectedId}
                onSelect={props.onSelect}
              />
            ) : (
              <NavigateClubItem key={club.id} club={club} />
            ),
          )}
        </ul>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <span>Coming soon</span>
        </div>
        <ul className={styles.disabledList}>
          {COMING_SOON.map((label) => (
            <li key={label} className={styles.disabledItem}>
              <span className={styles.disabledDot} />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      </div>

      <UserMenu
        currentSurface="cockpit"
        accessibleSurfaces={surfaces}
        placement="sidebar"
      />
    </aside>
  );
}

interface ClubAvatarProps {
  club: OrganizerClub;
}

function ClubAvatar({ club }: ClubAvatarProps) {
  return (
    <span
      className={`${styles.avatar} ${styles[`accent_${clubAccent(club.id)}`]}`}
      aria-hidden
    >
      {club.image ? (
        <img className={styles.avatarImage} src={club.image} alt="" />
      ) : (
        clubInitials(club.name)
      )}
    </span>
  );
}

interface PreviewClubItemProps {
  club: OrganizerClub;
  isActive: boolean;
  onSelect: (id: string) => void;
}

function PreviewClubItem({ club, isActive, onSelect }: PreviewClubItemProps) {
  return (
    <li>
      <button
        type="button"
        className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
        onClick={() => onSelect(club.id)}
        aria-current={isActive ? "true" : undefined}
      >
        <ClubAvatar club={club} />
        <span className={styles.itemBody}>
          <span className={styles.itemTitle}>{club.name}</span>
          <span className={styles.itemSub}>Click to preview</span>
        </span>
      </button>
    </li>
  );
}

interface NavigateClubItemProps {
  club: OrganizerClub;
}

function NavigateClubItem({ club }: NavigateClubItemProps) {
  const params = useParams({ strict: false }) as {
    id?: string;
    roomId?: string;
  };
  const isActive = params.id === club.id;

  return (
    <li>
      <Link
        to="/clubs/$id"
        params={{ id: club.id }}
        className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
        aria-current={isActive ? "true" : undefined}
      >
        <ClubAvatar club={club} />
        <span className={styles.itemBody}>
          <span className={styles.itemTitle}>{club.name}</span>
          <span className={styles.itemSub}>
            {isActive ? "Active" : "Open club"}
          </span>
        </span>
      </Link>
      {isActive && (
        <ActiveClubSubTabs clubId={club.id} activeRoomId={params.roomId} />
      )}
    </li>
  );
}

interface ActiveClubSubTabsProps {
  clubId: string;
  activeRoomId: string | undefined;
}

function ActiveClubSubTabs({ clubId, activeRoomId }: ActiveClubSubTabsProps) {
  const { data } = useClubDetails(clubId);
  const rooms = data?.readingRooms ?? [];

  return (
    <ul className={styles.subList}>
      {rooms.length > 0 && (
        <>
          <li className={styles.subGroupLabel}>Reading rooms</li>
          {rooms.map((room) => {
            const label = room.book?.title ?? room.name ?? "Untitled room";
            const isActive = activeRoomId === room.id;
            return (
              <li key={room.id}>
                <Link
                  to="/clubs/$id/rooms/$roomId"
                  params={{ id: clubId, roomId: room.id }}
                  className={`${styles.subLink} ${
                    isActive ? styles.subLinkActive : ""
                  }`}
                  aria-current={isActive ? "page" : undefined}
                  title={label}
                >
                  {label}
                </Link>
              </li>
            );
          })}
        </>
      )}
      <li>
        <Link
          to="/clubs/$id/rooms/new"
          params={{ id: clubId }}
          className={styles.subLink}
          activeProps={{
            className: `${styles.subLink} ${styles.subLinkActive}`,
          }}
        >
          New reading room
        </Link>
      </li>
      <li>
        <Link
          to="/clubs/$id/voting"
          params={{ id: clubId }}
          className={styles.subLink}
          activeProps={{
            className: `${styles.subLink} ${styles.subLinkActive}`,
          }}
        >
          Voting
        </Link>
      </li>
      <li>
        <Link
          to="/clubs/$id/gatherings"
          params={{ id: clubId }}
          className={styles.subLink}
          activeProps={{
            className: `${styles.subLink} ${styles.subLinkActive}`,
          }}
        >
          Gatherings
        </Link>
      </li>
    </ul>
  );
}

interface AppShellProps {
  sidebar: ReactNode;
  children: ReactNode;
}

export function AppShell({ sidebar, children }: AppShellProps) {
  return (
    <div className={styles.shell}>
      {sidebar}
      <main className={styles.main}>{children}</main>
    </div>
  );
}
